import React, { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const SITE = "https://abnormalworld.it";

export default function ShareButton({ trail }) {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedCaption, setCopiedCaption] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (!ref.current) return;
            if (ref.current.contains(e.target)) return;
            setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    if (!trail) return null;

    const url = `${SITE}/trail/${trail.slug}`;
    const tagline = `${trail.name} — ${trail.summary || "MTB Enduro all'Argentario"}`;
    const captionIG = `${trail.name.toUpperCase()} / Argentario\n\n${trail.summary || ""}\n\n📍 ${trail.length_km} km · D− ${trail.elevation_m} m · ${trail.difficulty?.toUpperCase()}\n\nCosa dicono i rider, meteo e mappa sul sito (link in bio).\n\n#mtbargentario #enduro #abnormalworld #montagentario #toscana #mtbitaly`;

    const tryNativeShare = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try { await navigator.share({ title: trail.name, text: tagline, url }); return true; }
            catch { /* user cancelled */ }
        }
        return false;
    };

    const copy = async (text, setFlag) => {
        try {
            await navigator.clipboard.writeText(text);
            setFlag(true);
            toast.success(t("common.copied"));
            setTimeout(() => setFlag(false), 2500);
        } catch {
            toast.error(t("common.copyFail"));
        }
    };

    const handleClick = async () => {
        const used = await tryNativeShare();
        if (!used) setOpen((o) => !o);
    };

    const waUrl = `https://wa.me/?text=${encodeURIComponent(`${tagline}\n${url}`)}`;
    const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(tagline)}`;
    const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(tagline)}`;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

    return (
        <div ref={ref} className="relative inline-block">
            <button
                type="button"
                onClick={handleClick}
                data-testid="share-trail-btn"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-abnormal-red hover:text-abnormal-red text-xs uppercase tracking-[0.25em] font-bold px-4 py-2.5 transition-colors"
            >
                <Share2 size={14} /> {t("common.share")}
            </button>

            {open && (
                <div data-testid="share-menu" className="absolute right-0 mt-2 w-72 bg-zinc-950 border border-white/15 shadow-2xl z-50">
                    <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-abnormal-red font-bold">{t("common.shareTrail")}</span>
                        <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" aria-label="close"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-white/5">
                        <a href={waUrl} target="_blank" rel="noreferrer" data-testid="share-wa" className="bg-zinc-950 hover:bg-abnormal-red hover:text-white px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">WhatsApp</a>
                        <a href={tgUrl} target="_blank" rel="noreferrer" data-testid="share-tg" className="bg-zinc-950 hover:bg-abnormal-red hover:text-white px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">Telegram</a>
                        <a href={xUrl} target="_blank" rel="noreferrer" data-testid="share-x" className="bg-zinc-950 hover:bg-abnormal-red hover:text-white px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">X / Twitter</a>
                        <a href={fbUrl} target="_blank" rel="noreferrer" data-testid="share-fb" className="bg-zinc-950 hover:bg-abnormal-red hover:text-white px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">Facebook</a>
                    </div>
                    <button
                        onClick={() => copy(url, setCopiedLink)}
                        data-testid="share-copy-link"
                        className="w-full flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs uppercase tracking-wider font-bold hover:bg-white/5 transition-colors"
                    >
                        <span>{t("common.copyLink")}</span>
                        {copiedLink ? <Check size={14} className="text-abnormal-red" /> : <Copy size={14} />}
                    </button>
                    <button
                        onClick={() => copy(captionIG, setCopiedCaption)}
                        data-testid="share-copy-caption"
                        className="w-full flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs uppercase tracking-wider font-bold hover:bg-white/5 transition-colors"
                        title="Pronta per Instagram / TikTok"
                    >
                        <span>{t("common.copyCaption")}</span>
                        {copiedCaption ? <Check size={14} className="text-abnormal-red" /> : <Copy size={14} />}
                    </button>
                </div>
            )}
        </div>
    );
}
